<template>
  <div>
    <div class="mb-3">
      <label class="form-label text-capitalize-first">{{ helper.trans('labels.traders') }}</label>
      <select
        class="form-select form-select-sm"
        :disabled="disabled"
        :value="modelValue.traderScope"
        @change="onTraderScopeChange($event)"
      >
        <option v-for="scope in traderScopes" :key="scope" :value="scope">
          {{ helper.trans(`notification_center.trader_scope.${scope}`) }}
        </option>
      </select>
      <div v-if="modelValue.traderScope === 'selected'" class="mt-2">
        <TraderMultiselect
          :disabled="disabled"
          :multiple="true"
          :small="true"
          v-model="selectedTraders"
        />
      </div>
    </div>

    <div class="mb-3">
      <label class="form-label text-capitalize-first">{{ helper.trans('labels.merchants') }}</label>
      <select
        class="form-select form-select-sm"
        :disabled="disabled"
        :value="modelValue.merchantScope"
        @change="onMerchantScopeChange($event)"
      >
        <option v-for="scope in merchantScopes" :key="scope" :value="scope">
          {{ helper.trans(`notification_center.merchant_scope.${scope}`) }}
        </option>
      </select>
      <div v-if="modelValue.merchantScope === 'selected'" class="mt-2">
        <MerchantMultiselect
          :disabled="disabled"
          :multiple="true"
          :small="true"
          v-model="selectedMerchants"
        />
      </div>
    </div>

    <div class="mb-3">
      <label class="form-label text-capitalize-first">{{ helper.trans('notification_center.merchant_tg_groups') }}</label>
      <MerchantTgGroupMultiselect
        :disabled="disabled"
        v-model="selectedMerchantGroups"
      />
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent, Prop, PropType, ref, watch} from 'vue'
import {helper} from "@/service/CommonHelper";
import TraderMultiselect from "@/components/TraderMultiselect.vue";
import MerchantMultiselect from "@/components/MerchantMultiselect.vue";
import MerchantTgGroupMultiselect from "@/components/MerchantTgGroupMultiselect.vue";
import TelegramBroadcastAudienceSpec, {
  MerchantScope,
  normalizeTelegramBroadcastSpec,
  TraderScope,
} from "@/models/TelegramBroadcastAudienceSpec";
import TraderDTO from "@/models/TraderDTO";
import MerchantDTO from "@/models/MerchantDTO";
import TelegramBroadcastMerchantGroupDTO from "@/models/TelegramBroadcastMerchantGroupDTO";

const traderScopes: TraderScope[] = ['none', 'all', 'enabled', 'selected'];
const merchantScopes: MerchantScope[] = ['none', 'all', 'not_blocked', 'selected'];

export default defineComponent({
  name: 'TelegramBroadcastAudienceSpecForm',
  components: {
    TraderMultiselect,
    MerchantMultiselect,
    MerchantTgGroupMultiselect,
  },
  props: {
    modelValue: {
      type: Object as PropType<TelegramBroadcastAudienceSpec>,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue'],
  setup(props, {emit}) {
    const selectedTraders = ref<TraderDTO[]>([]);
    const selectedMerchants = ref<MerchantDTO[]>([]);
    const selectedMerchantGroups = ref<TelegramBroadcastMerchantGroupDTO[]>([]);

    const emitSpec = (patch: Partial<TelegramBroadcastAudienceSpec>) => {
      emit('update:modelValue', normalizeTelegramBroadcastSpec({...props.modelValue, ...patch}));
    };

    watch(selectedTraders, (traders) => {
      emitSpec({traderIds: (traders ?? []).map(t => t.id)});
    }, {deep: true});

    watch(selectedMerchants, (merchants) => {
      emitSpec({merchantIds: (merchants ?? []).map(m => m.id)});
    }, {deep: true});

    watch(selectedMerchantGroups, (groups) => {
      emitSpec({merchantTgGroupIds: (groups ?? []).map(g => g.id)});
    }, {deep: true});

    const onTraderScopeChange = (event: Event) => {
      const traderScope = (event.target as HTMLSelectElement).value as TraderScope;
      const patch: Partial<TelegramBroadcastAudienceSpec> = {traderScope};
      if (traderScope !== 'selected') {
        selectedTraders.value = [];
        patch.traderIds = [];
      }
      emitSpec(patch);
    };

    const onMerchantScopeChange = (event: Event) => {
      const merchantScope = (event.target as HTMLSelectElement).value as MerchantScope;
      const patch: Partial<TelegramBroadcastAudienceSpec> = {merchantScope};
      if (merchantScope !== 'selected') {
        selectedMerchants.value = [];
        patch.merchantIds = [];
      }
      emitSpec(patch);
    };

    return {
      helper,
      traderScopes,
      merchantScopes,
      selectedTraders,
      selectedMerchants,
      selectedMerchantGroups,
      onTraderScopeChange,
      onMerchantScopeChange,
    };
  },
})
</script>
